import { queryClient } from '@/router'
import { useMutation } from '@tanstack/react-query'
import { type FC, useCallback } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/button/button'
import { Checkbox } from '@/components/checkbox/checkbox'

import { API_ENDPOINTS } from '@/lib/constants/api'
import { EXTERNAL_URLS } from '@/lib/constants/url'
import { updateCaseInquirySubmissionMutation } from '@/lib/queries/business/v1/case-extra/mutations'
import { getCaseExtraInfoQuery } from '@/lib/queries/business/v3/case-extra/queries'
import { cn } from '@/lib/utils'
import { doPolling } from '@/lib/utils/function'

interface Props {
  className?: string
  caseId: number
  submitted?: boolean
}

export const SubmitCaseInquiry: FC<Props> = ({ className, caseId, submitted = false }) => {
  const { mutateAsync: updateCaseInquirySubmission, isPending: isUpdatingCaseInquirySubmission } = useMutation(
    updateCaseInquirySubmissionMutation
  )

  const handleCheckedChange = useCallback(
    async (checked: boolean) => {
      try {
        await updateCaseInquirySubmission(
          {
            caseId: caseId,
            inquirySubmitted: checked,
          },
          {
            onSuccess: () => {
              void queryClient.invalidateQueries(getCaseExtraInfoQuery(caseId))

              doPolling({
                fn: () => {
                  void queryClient.invalidateQueries({
                    queryKey: [API_ENDPOINTS.business.v4.case.search.index],
                  })
                },
                times: 4,
                interval: 2000,
              })
            },
          }
        )
      } catch (err) {
        toast.error('Failed to update.')
      }
    },
    [caseId, updateCaseInquirySubmission]
  )

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Button
        className={cn('h-8 w-full', 'select-none text-xs font-medium')}
        disabled={isUpdatingCaseInquirySubmission || submitted}
        asChild={!(isUpdatingCaseInquirySubmission || submitted)}
      >
        <a href={EXTERNAL_URLS.caseInquiry} target="_blank" rel="noreferrer" tabIndex={-1}>
          Case Inquiry
        </a>
      </Button>

      <label
        className={cn(
          'flex items-center gap-2',
          'transition-opacity duration-150',
          isUpdatingCaseInquirySubmission && 'opacity-50'
        )}
      >
        <Checkbox
          className={cn('border-border focus:border-border data-[state=checked]:border-primary')}
          checked={submitted}
          loading={isUpdatingCaseInquirySubmission}
          onCheckedChange={handleCheckedChange}
        />
        <span className={cn('select-none text-xs font-medium leading-5 text-slate-500')}>
          Did you submit a Case Inquiry?
        </span>
      </label>
    </div>
  )
}
